import { useState } from "react";
import { useStravaOAuth } from "../hooks/use-strava-oauth";
import styles from "./strava-connect-button.module.css";

interface StravaConnectButtonProps {
  /** URL path to return to after OAuth completes */
  returnTo?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Optional callback when connect button is clicked (before OAuth redirect) */
  onConnect?: () => void;
  /** Optional toolbox to select after OAuth return (e.g., "lineStyle") */
  selectToolbox?: string;
}

/**
 * Strava Connect button following Strava brand guidelines.
 * Initiates the OAuth flow when clicked.
 */
export const StravaConnectButton = ({
  returnTo = "/",
  className,
  onConnect,
  selectToolbox,
}: StravaConnectButtonProps) => {
  const { initiateOAuth } = useStravaOAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleConnect = () => {
    setIsLoading(true);
    onConnect?.();
    // Add toolbox selection to return URL if specified
    const returnUrl = selectToolbox
      ? `${returnTo}?selectToolbox=${selectToolbox}`
      : returnTo;
    // Small delay to show loading state before redirect
    setTimeout(() => {
      initiateOAuth(returnUrl);
    }, 100);
  };

  return (
    <button
      className={`${styles.button} ${className ?? ""}`}
      onClick={handleConnect}
      disabled={isLoading}
      aria-label="Connect with Strava"
    >
      {isLoading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        <span className={styles.icon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
          </svg>
        </span>
      )}
      <span>{isLoading ? "Connecting..." : "Connect with Strava"}</span>
    </button>
  );
};
